import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import moment from 'moment';

import Utils from '../../Utils';
import {
	toEnglishActivityValue,
	toEnglishAmountValue,
	toEnglishJobTitleValue,
	toEnglishPhysicalSourceValue,
	toEnglishSectorValue,
} from '../../Assets/OptionsTranslations';

import { IUsePhysicalInvestorForm } from './IUsePhysicalInvestorForm';
import { DefaultValue, InvestorDTO, InvestorErrorsDTO } from '../../Models';

import { emptyInvestorErrors } from '../../Assets/Data/EmptyValues';

export const usePhysicalInvestorForm = ({
	translations,
	investorAPI,
	placesService,
	unlockNextStep,
	getPlacePredictions,
	contractAPI,
}: IUsePhysicalInvestorForm) => {
	const normalizeEmploymentValue = (value?: string) => {
		if (!value) {
			return '';
		}

		return value
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim()
			.toLowerCase();
	};

	const isEmployeeSituation = (situation?: string) => {
		const normalizedSituation = normalizeEmploymentValue(situation);
		if (!normalizedSituation) {
			return false;
		}

		const employeeValues = ['Cuenta ajena', 'Employee', 'Empregado'];
		const normalizedTranslatedEmployee = normalizeEmploymentValue(translations.employee);

		return normalizedSituation === normalizedTranslatedEmployee
			|| employeeValues.some((value) => normalizeEmploymentValue(value) === normalizedSituation);
	};

	const [searchParams] = useSearchParams();

	const [loading, setLoading] = useState<boolean>(false);
	const [noContent, setNoContent] = useState<boolean>(false);
	const [nationalitiesList, setNationalitiesList] = useState<any[]>([]);
	const [investorsData, setInvestorsData] = useState<InvestorDTO[]>([]);
	const [contractLanguage, setContractLanguage] = useState<string>('es');

	const [errors, setErrors] = useState<InvestorErrorsDTO[]>([]);
	const [hasErrors, setHasErrors] = useState<boolean>(true);

	const [activityInList, setActivityInList] = useState<boolean>(true);
	const [contractId, setContractId] = useState<number>(0);
	const [taxAddressInputs, setTaxAddressInputs] = useState<string[]>([]);
	const [mainAddressInputs, setMainAddressInputs] = useState<string[]>([]);
	const [manualAddressVisibility, setManualAddressVisibility] = useState<Record<number, { mainResidence: boolean; taxResidence: boolean }>>({});

	const [activeAddress, setActiveAddress] = useState<{ index: number, field: string } | null>(null);
	const [defaultValues, setDefaultValues] = useState<DefaultValue[]>([]);

	const updateManualAddressVisibility = (
		index: number,
		field: 'mainResidence' | 'taxResidence',
		visible: boolean,
	) => {
		setManualAddressVisibility((prevState) => ({
			...prevState,
			[index]: {
				mainResidence: prevState[index]?.mainResidence ?? false,
				taxResidence: prevState[index]?.taxResidence ?? false,
				[field]: visible,
			},
		}));
	};

	const configureInitialData = (ContractId: number) => {
		setLoading(true);
		setContractId(ContractId);
		investorAPI.getDefaultValuesInvestor(ContractId)
			.then((resp: any) => {
				if (resp.success && resp.data) {
					const newDefaultValues: DefaultValue[] = resp.data;
					setDefaultValues(newDefaultValues);
					investorAPI.getInvestors(ContractId).then((res: any) => {
						if (res.success) {
							if (res.data.length > 0) {
								res.data = res.data.map((investor: InvestorDTO, index: number) => {
									setMainAddressInputs((prevState: string[]) => {
										const updatedInputs = [...prevState];
										updatedInputs[index] = `${investor.mainResidence}, ${investor.mainResidencePostalCode}, `
											+ `${investor.mainResidenceCity}, ${investor.mainResidenceCountry}`;
										return updatedInputs;
									});
									if (investor.taxResidence) {
										setTaxAddressInputs((prevState: string[]) => {
											const updatedInputs = [...prevState];
											updatedInputs[index] = `${investor.taxResidence}, ${investor.taxResidencePostalCode}, `
												+ `${investor.taxResidenceCity}, ${investor.countryTaxResidence}`;
											return updatedInputs;
										});
									}

									const updatedInvestor = { ...investor };

									if (updatedInvestor.dni) updatedInvestor.documentType = 'dni';
									if (updatedInvestor.nif) updatedInvestor.documentType = 'nif';
									if (updatedInvestor.passport) updatedInvestor.documentType = 'passport';
									if (updatedInvestor.residenceCard) updatedInvestor.documentType = 'residenceCard';
									if ((updatedInvestor.mainResidence === updatedInvestor.taxResidence)
										&& (updatedInvestor.mainResidenceCity === updatedInvestor.taxResidenceCity)
										&& (updatedInvestor.mainResidencePostalCode === updatedInvestor.taxResidencePostalCode)
										&& (updatedInvestor.mainResidenceCountry === updatedInvestor.countryTaxResidence)) {
										updatedInvestor.sameAddresses = true;
									} else {
										updatedInvestor.sameAddresses = false;
									}

									if (investor.prp) {
										investor.hasPRP = true;
									}

									newDefaultValues.forEach((defVal: DefaultValue) => {
										if (updatedInvestor.hasOwnProperty(defVal.field) && defVal.value && updatedInvestor[defVal.field] === "") {
											(updatedInvestor as any)[defVal.field] = defVal.value;
										}
									});

									updatedInvestor.situation = toEnglishActivityValue(updatedInvestor.situation);
									updatedInvestor.incomes = toEnglishAmountValue(updatedInvestor.incomes);
									updatedInvestor.ocupation = toEnglishJobTitleValue(updatedInvestor.ocupation);
									updatedInvestor.source = toEnglishPhysicalSourceValue(updatedInvestor.source);
									updatedInvestor.sector = toEnglishSectorValue(updatedInvestor.sector);
									errors.push({ ...emptyInvestorErrors });
									return updatedInvestor;
								});

								validateData(res.data, false);
								setInvestorsData(res.data);
							}
						} else {
							setNoContent(true);
						}
						setLoading(false);
					}).catch((err: any) => {
						console.log(err);
					});
				}
			});
		setLoading(false);
		contractAPI.getContract(ContractId).then((res: any) => {
			if (res.success) {
				if (res.data?.idLanguage) {
					setContractLanguage(res.data.idLanguage === 1 ? 'es' : 'en');
				}
			}
		});
	};

	const getNationalities = () => {
		investorAPI.getCountries()
			.then((res: any) => {
				if (res !== '') {
					setNationalitiesList(res);
				}
			})
			.catch((err: any) => {
				console.log(err);
			});
	};

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = (hash: string) => (
				contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					}
				})
			);

			getContractIdByHash(hash!);
		}

		getNationalities();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [translations]);

	const handleChangeData = (index: number) => (e: any) => {
		const { name, value } = e.target;
		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[name]: false,
			};
			return updatedErrors;
		});

		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: value,
			};
			return updatedInvestors;
		});
	};

	const handleChangeDate = (field: string, index: number) => (date: any) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined,
			};
			return updatedInvestors;
		});
		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
	};

	const onChangeDate = (field: string, index: number) => (date: any) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined,
			};
			return updatedInvestors;
		});
		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
	};

	const handleSelectData = (field: string, index?: number) => (value: string) => {
		if (field === 'sector') setActivityInList(value !== 'Otro');

		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
				employerName: field === 'situation' ? false : updatedErrors[index || 0]?.employerName,
			};
			return updatedErrors;
		});
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index || 0] = {
				...updatedInvestors[index || 0],
				[field]: value,
			};
			return updatedInvestors;
		});
	};

	const handleCheckedData = (index: number) => (e: any) => {
		const { name } = e.target;
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: e.target.checked,
			};
			return updatedInvestors;
		});
	};

	const validateData = (investorsData: InvestorDTO[], updateAddressVisibility = true) => {
		let isOK = true;
		investorsData.map((investorData: InvestorDTO, index: number) => {
			const investorErrors: InvestorErrorsDTO = { ...emptyInvestorErrors };
			if (!investorData.birthDate) {
				investorErrors.birthDate = true;
				isOK = false;
			}
			if (!investorData.birthPlace) {
				investorErrors.birthPlace = true;
				isOK = false;
			}
			if (!investorData.nationality) {
				investorErrors.nationality = true;
				isOK = false;
			}
			if (
				!investorData.mainResidence
				|| !investorData.mainResidenceCity
				|| !investorData.mainResidencePostalCode
				|| !investorData.mainResidenceCountry
			) {
				investorErrors.mainResidence = true;
				investorErrors.mainResidenceCity = !investorData.mainResidenceCity;
				investorErrors.mainResidencePostalCode = !investorData.mainResidencePostalCode;
				investorErrors.mainResidenceCountry = !investorData.mainResidenceCountry;
				if (updateAddressVisibility) {
					updateManualAddressVisibility(index, 'mainResidence', true);
				}
				isOK = false;
			} else {
				if (updateAddressVisibility) {
					updateManualAddressVisibility(index, 'mainResidence', false);
				}
			}
			if (!investorData.emailContact || !Utils.validate('email', investorData.emailContact)) {
				investorErrors.emailContact = true;
				isOK = false;
			}
			if (!investorData.phoneNumber) {
				investorErrors.phoneNumber = true;
				isOK = false;
			}
			if (!investorData.phonePrefix) {
				investorErrors.phonePrefix = true;
				isOK = false;
			}
			if (!investorData.documentType) {
				investorErrors.documentType = true;
				isOK = false;
			}
			const document = investorData.dni
				|| investorData.nif
				|| investorData.passport
				|| investorData.residenceCard;
			if (!document) {
				investorErrors.document = true;
				isOK = false;
			} else if (!/^[A-Z0-9]*$/g.test(document!)) {
				investorErrors.documentFormat = true;
				isOK = false;
			}
			if (!investorData.docValidThru) {
				investorErrors.documentExpiration = true;
				isOK = false;
			} else if (moment(investorData.docValidThru).isBefore(moment())) {
				investorErrors.notValidExpirationDate = true;
				isOK = false;
			}
			if (
				!investorData.sameAddresses
				&& (
					!investorData.taxResidence
					|| !investorData.taxResidenceCity
					|| !investorData.taxResidencePostalCode
					|| !investorData.countryTaxResidence
				)
			) {
				investorErrors.taxResidence = true;
				investorErrors.taxResidenceCity = !investorData.taxResidenceCity;
				investorErrors.taxResidencePostalCode = !investorData.taxResidencePostalCode;
				investorErrors.countryTaxResidence = !investorData.countryTaxResidence;
				if (updateAddressVisibility) {
					updateManualAddressVisibility(index, 'taxResidence', true);
				}
				isOK = false;
			} else {
				if (updateAddressVisibility) {
					updateManualAddressVisibility(index, 'taxResidence', false);
				}
			}
			if (!investorData.ocupation) {
				investorErrors.ocupation = true;
				isOK = false;
			}
			if ((investorData.sector === 'Otro' && !investorData.otherSector) || !investorData.sector) {
				investorErrors.sector = true;
				isOK = false;
			}
			if (investorData.hasPRP && !investorData.prp) {
				investorErrors.prp = true;
				isOK = false;
			}
			if (!investorData.bankDeposits && index === 0) {
				investorErrors.bankDeposits = true;
				isOK = false;
			}
			if (!investorData.bankAddress && index === 0) {
				investorErrors.bankAddress = true;
				isOK = false;
			}
			if (!investorData.iban && index === 0) {
				investorErrors.iban = true;
				isOK = false;
			}
			if (!investorData.biC_SWIFT && index === 0) {
				investorErrors.biC_SWIFT = true;
				isOK = false;
			}
			if (!investorData.situation) {
				investorErrors.situation = true;
				isOK = false;
			}
			if (isEmployeeSituation(investorData.situation) && !investorData.employerName?.trim()) {
				investorErrors.employerName = true;
				isOK = false;
			}
			if (!investorData.incomes) {
				investorErrors.incomes = true;
				isOK = false;
			}
			if (!investorData.source) {
				investorErrors.source = true;
				isOK = false;
			}
			setErrors((prevErrors) => {
				const newErrors = [...prevErrors];
				newErrors[index] = investorErrors;
				return newErrors;
			});
			setHasErrors(!isOK);

			return investorData;
		});

		return isOK;
	};

	const saveData = () => {
		setLoading(true);
		if (validateData(investorsData)) {
			const copyInvestorsData = investorsData.map((investorData: InvestorDTO) => {
				const updatedInvestor = { ...investorData };
				updatedInvestor.situation = toEnglishActivityValue(updatedInvestor.situation);
				updatedInvestor.incomes = toEnglishAmountValue(updatedInvestor.incomes);
				updatedInvestor.ocupation = toEnglishJobTitleValue(updatedInvestor.ocupation);
				updatedInvestor.source = toEnglishPhysicalSourceValue(updatedInvestor.source);
				updatedInvestor.sector = toEnglishSectorValue(updatedInvestor.sector);
				updatedInvestor.sector = updatedInvestor.sector === 'Otro'
					? updatedInvestor.otherSector : updatedInvestor.sector;
				updatedInvestor.docValidThru = moment(updatedInvestor.docValidThru).format('YYYY-MM-DD');
				updatedInvestor.birthDate = moment(updatedInvestor.birthDate).format('YYYY-MM-DD');
				if (updatedInvestor.sameAddresses) {
					updatedInvestor.taxResidence = updatedInvestor.mainResidence;
					updatedInvestor.taxResidenceCity = updatedInvestor.mainResidenceCity;
					updatedInvestor.taxResidencePostalCode = updatedInvestor.mainResidencePostalCode;
					updatedInvestor.countryTaxResidence = updatedInvestor.mainResidenceCountry;
					updatedInvestor.taxResidenceExtra = updatedInvestor.mainResidenceExtra;
				}
				return updatedInvestor;
			});
			investorAPI.updateInvestors(copyInvestorsData).then((res: any) => {
				if (res.success) {
					message.success(translations.datosGuardados);
					unlockNextStep(contractId);
				} else {
					message.error(translations.messageError);
				}
				setLoading(false);
			});
		} else {
			setLoading(false);
		}
	};

	const onChangeSameAddresses = (index: number) => (checked: boolean) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				sameAddresses: checked,
			};
			return updatedInvestors;
		});
		if (checked) {
			updateManualAddressVisibility(index, 'taxResidence', false);
		}
	};

	const getAddressComponent = (placeDetails: any, type: string) => {
		const component = placeDetails?.address_components?.find((component: any) => component.types.includes(type));
		return component ? component.long_name : '';
	};

	const getFormattedStreetAddress = (placeDetails: any, fallbackValue: string) => {
		const route = getAddressComponent(placeDetails, 'route');
		const streetNumber = getAddressComponent(placeDetails, 'street_number');
		const premise = getAddressComponent(placeDetails, 'premise');
		const subpremise = getAddressComponent(placeDetails, 'subpremise');
		const pointOfInterest = getAddressComponent(placeDetails, 'point_of_interest');
		const neighborhood = getAddressComponent(placeDetails, 'neighborhood');
		const placeName = placeDetails?.name?.trim?.() || '';
		const fallbackStreet = fallbackValue
			.split(',')[0]
			.split(' - ')[0]
			.trim();

		return [route, streetNumber].filter(Boolean).join(', ')
			|| premise
			|| subpremise
			|| placeName
			|| pointOfInterest
			|| neighborhood
			|| fallbackStreet;
	};

	const handleSelectAddress = (index: number, field: string) => (value: string, option: any) => {
		if (!activeAddress) return;
		placesService?.getDetails(
			{ placeId: option.key },
			(placeDetails: any) => {
				const address = getFormattedStreetAddress(placeDetails, value);
				const city = getAddressComponent(placeDetails, 'locality')
					|| getAddressComponent(placeDetails, 'administrative_area_level_2');
				const postalCode = getAddressComponent(placeDetails, 'postal_code');
				const country = getAddressComponent(placeDetails, 'country');

				setInvestorsData((prevState: InvestorDTO[]) => {
					const updatedInvestors = [...prevState];
					const updatedInvestor = { ...updatedInvestors[index] };

					if (field === 'mainResidence') {
						setMainAddressInputs((prevState: string[]) => {
							const updatedInputs = [...prevState];
							updatedInputs[index] = value;
							return updatedInputs;
						});
						updatedInvestor.mainResidence = address;
						updatedInvestor.mainResidenceCity = city;
						updatedInvestor.mainResidencePostalCode = postalCode;
						updatedInvestor.mainResidenceCountry = country;
					} else if (field === 'taxResidence') {
						setTaxAddressInputs((prevState: string[]) => {
							const updatedInputs = [...prevState];
							updatedInputs[index] = value;
							return updatedInputs;
						});
						updatedInvestor.taxResidence = address;
						updatedInvestor.taxResidenceCity = city;
						updatedInvestor.taxResidencePostalCode = postalCode;
						updatedInvestor.countryTaxResidence = country;
					}

					updatedInvestors[index] = updatedInvestor;
					return updatedInvestors;
				});
				setErrors((prevState: InvestorErrorsDTO[]) => {
					const updatedErrors = [...prevState];
					if (field === 'mainResidence') {
						updatedErrors[index] = {
							...updatedErrors[index],
							mainResidence: !address || !city || !postalCode || !country,
							mainResidenceCity: !city,
							mainResidencePostalCode: !postalCode,
							mainResidenceCountry: !country,
						};
					} else if (field === 'taxResidence') {
						updatedErrors[index] = {
							...updatedErrors[index],
							taxResidence: !address || !city || !postalCode || !country,
							taxResidenceCity: !city,
							taxResidencePostalCode: !postalCode,
							countryTaxResidence: !country,
						};
					}
					return updatedErrors;
				});
				if (field === 'mainResidence') {
					updateManualAddressVisibility(index, 'mainResidence', !address || !city || !postalCode || !country);
				} else if (field === 'taxResidence') {
					updateManualAddressVisibility(index, 'taxResidence', !address || !city || !postalCode || !country);
				}
			},
		);
	};

	const handleSearchAddress = (index: number, field: string) => (value: string) => {
		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
		setActiveAddress({ index, field });
		getPlacePredictions({ input: value });
	};

	const onChangeHasPRP = (index: number) => (checked: boolean) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				hasPRP: checked
			};
			return updatedInvestors;
		});
		setErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				prp: false
			};
			return updatedErrors;
		});
	};

	const onChangeNotificationsEnabled = (index: number) => (checked: boolean) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				notificationsEnabled: checked
			};
			return updatedInvestors;
		});
	};

	return {
		loading,
		noContent,
		errors,
		hasErrors,
		investorsData,
		nationalitiesList,
		activityInList,
		contractId,
		contractLanguage,
		mainAddressInputs,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		defaultValues,
		onChangeDate,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		handleCheckedData,
		saveData,
		onChangeSameAddresses,
		handleSelectAddress,
		handleSearchAddress,
		onChangeHasPRP,
		onChangeNotificationsEnabled,
		isEmployeeSituation
	};
};
